import { PATH_AUTH } from "config";

const STORAGE_KEY = "rivetai:pending-mcp-authorization";
/** Matches the backend's signed request lifetime; a stale entry is useless. */
const MAX_AGE_MS = 10 * 60 * 1000;

type PendingAuthorization = { query: string; savedAt: number };

/**
 * An AI app's authorization request, saved while the user signs in.
 *
 * Sign-in has several paths (password, MFA, passkey, Google, SSO) and most of
 * them land on the dashboard. The dashboard redirect handler picks this up and
 * returns to the consent screen. localStorage, not sessionStorage, because
 * logout clears sessionStorage and SSO can come back in a fresh tab.
 */
export const mcpAuthorizationStorage = {
  save(query: string): void {
    const trimmed = query.replace(/^\?/, "");
    if (!trimmed) return;
    try {
      const value: PendingAuthorization = { query: trimmed, savedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Storage can be unavailable (private mode); the user can restart from their app.
    }
  },

  /** Pending query string without "?", or null when missing or expired. */
  get(now: number = Date.now()): string | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw) as Partial<PendingAuthorization>;
      if (typeof value.query !== "string" || typeof value.savedAt !== "number") {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      if (now - value.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return value.query;
    } catch {
      return null;
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  },
};

/** Consent screen path for a saved or current authorization query. */
export const buildMcpConsentPath = (query: string): string =>
  `${PATH_AUTH.mcpOAuthConsent}?${query.replace(/^\?/, "")}`;
